import React, { useState, useEffect } from 'react';
import { Timestamp } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

export interface Memory {
  id: string;
  title: string;
  content: string;
  tags: string[];  // Changed from optional to required but can be empty array
  isPublic: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  userId: string;
  userDisplayName?: string;
  userIcon?: string;
  authorName?: string;
}

interface MemoryCardProps {
  memory: Memory;
  onClick: () => void;
}

const MemoryCard: React.FC<MemoryCardProps> = ({ memory, onClick }) => {
  const [userIcon, setUserIcon] = useState<string>('🌙');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserIcon = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', memory.userId));
        if (userDoc.exists()) {
          const icon = userDoc.data().icon || '🌙';
          console.log(`Fetched icon for user ${memory.userId}: ${icon}`);
          setUserIcon(icon);
        } else {
          console.log(`No user document found for ${memory.userId}`);
        }
      } catch (error) {
        console.error(`Error fetching user icon for ${memory.userId}:`, error);
      }
    };

    fetchUserIcon();
  }, [memory.userId]);

  // Defensive check for required properties
  if (!memory || !memory.id || !memory.title || !memory.content) {
    console.error('Invalid memory object:', memory);
    return null;
  }

  const handleUserClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the main card click
    navigate(`/profile/${memory.userId}`);
  };

  return (
    <div
      onClick={onClick}
      className="group relative p-4 sm:p-6 rounded-2xl
                bg-gradient-to-b from-dream-800/90 to-dream-900/90
                border border-dream-700/30 shadow-lg backdrop-blur-sm
                transition-all duration-300 ease-out
                hover:border-dream-600/50 hover:scale-[1.02] hover:-translate-y-1
                active:scale-[0.98] touch-action-manipulation
                animate-fade-in cursor-pointer
                overflow-hidden"
    >
      {/* Background animation - now contained within the card boundaries due to overflow-hidden */}
      <div className="absolute -z-10 inset-0 bg-gradient-to-br 
                    from-dream-500/5 via-transparent to-mist-500/5
                    rounded-2xl opacity-0 group-hover:opacity-50 transition-opacity duration-500
                    pointer-events-none [animation:spin_20s_linear_infinite]"></div>

      <div className="flex items-start justify-between mb-3 sm:mb-4">
        <div 
          onClick={handleUserClick}
          className="flex items-center gap-2 sm:gap-3 cursor-pointer hover:opacity-80 transition-opacity"
        >
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-dream-700/50 border border-dream-600/30
                       flex items-center justify-center text-base sm:text-xl overflow-hidden">
            {userIcon}
          </div>
          <div>
            <h3 className="text-base sm:text-lg font-bold text-dream-50 group-hover:text-dream-50/90
                        transition-colors duration-300 line-clamp-1">
              {memory.title || 'Untitled Dream'}
            </h3>
            <p className="text-xs text-dream-300 hover:text-dream-200 transition-colors">
              Dreamed by {memory.userDisplayName || memory.authorName || 'Anonymous Dreamer'}
            </p>
          </div>
        </div>
      </div>

      <p className="text-sm sm:text-base text-dream-200/90 line-clamp-3 mb-3 sm:mb-4">
        {memory.content || 'No content'}
      </p>

      {/* Tags */}
      {memory.tags && memory.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {memory.tags.map((tag, index) => (
            <span
              key={`${tag}-${index}`}
              className="px-2 py-0.5 text-xs rounded-full bg-dream-700/40
                     text-dream-50/90 border border-dream-600/20"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Public/Private indicator - Always at bottom right */}
      <div className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4">
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-dream-800/80 
                     border border-dream-700/30 backdrop-blur-sm">
          {memory.isPublic ? (
            <svg className="w-3.5 h-3.5 text-dream-300/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5 text-dream-300/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8V7a4 4 0 00-8 0v4h8z" />
            </svg>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemoryCard; 